import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import { DebounceInput } from "react-debounce-input";
import { toast } from "react-toastify";

import {
  ActionButton,
  AppTable,
  ColumnHeader,
  TableSpinner,
  TextColumn,
} from "../../components";
import { BreadCrumb } from "../../components/Common";
import DeleteModal from "../../components/Common/DeleteModal";
import {
  ActionTypeEnum,
  LiveLectureAccessModeEnum,
  LiveLectureStatusEnum,
  RoleEnum,
} from "../../enums";
import { LiveLectureDto, PaginateQuery } from "../../interfaces";
import { Paginated } from "../../models";
import { AccountState } from "../../store/slices/accountSlice";
import {
  useDeleteLiveLectureMutation,
  useGetLiveLecturesQuery,
  useLazyGetSessionMeetingLinkQuery,
} from "../../store/api/liveLectureApi";
import { AddEditPrivateLiveLectureModal } from "./AddEditPrivateLiveLectureModal";
import {
  formatLiveLectureDate,
  formatLiveLectureTimeSlot,
  canEditLiveLecture,
  canManageLiveLecture,
} from "./liveLectureCalendarUtils";
import { openLiveCall } from "./openLiveCall";

const joinableStatuses = [
  LiveLectureStatusEnum.PUBLISHED,
  LiveLectureStatusEnum.FULL,
  LiveLectureStatusEnum.IN_PROGRESS,
];

export const PrivateLiveLecturePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );
  const lang = i18n.language === "ar" ? "ar" : "en";

  const initState = new PaginateQuery(10, "start_at:DESC", {
    accessMode: LiveLectureAccessModeEnum.PRIVATE,
  });

  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [joiningId, setJoiningId] = useState<string | null>(null);

  const {
    data: response,
    isLoading,
    refetch,
  } = useGetLiveLecturesQuery(paginateQuery, {
    skip: profile?.role?.key !== RoleEnum.SUPER_ADMIN,
  });
  const [deleteLiveLecture, { isLoading: isLoadingDelete }] =
    useDeleteLiveLectureMutation();
  const [getSessionMeetingLink] = useLazyGetSessionMeetingLinkQuery();

  const isSuperAdmin = profile?.role?.key === RoleEnum.SUPER_ADMIN;

  const tableData = useMemo(
    () => (response as Paginated<LiveLectureDto>)?.data || [],
    [response],
  );

  const onAdd = () => {
    setEditingItemId(null);
    setShowAddEditModal(true);
  };

  const onEdit = (id: string) => {
    setEditingItemId(id);
    setShowAddEditModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingItemId(id);
    setShowDeleteModal(true);
  };

  const onJoin = async (row: LiveLectureDto) => {
    setJoiningId(row.id);
    try {
      const sessionLink = await getSessionMeetingLink(row.id).unwrap();
      if (sessionLink.canJoin && sessionLink.token) {
        const joined = await openLiveCall(sessionLink, history, {
          title: row.title?.[lang],
          returnPath: history.location.pathname,
        });
        if (!joined) {
          toast.error(t("liveCallMicPermissionDenied"));
        }
      } else {
        toast.error(t("joinLiveLectureNotAvailable"));
      }
    } catch {
      toast.error(t("joinLiveLectureError"));
    } finally {
      setJoiningId(null);
    }
  };

  const columns: TableColumn<LiveLectureDto>[] = useMemo(
    () => [
      {
        name: <ColumnHeader title="title" />,
        cell: (row) => (
          <TextColumn text={row.title[lang]} isNeedToTranslate={false} />
        ),
        sortable: true,
      },
      {
        name: <ColumnHeader title="time" />,
        cell: (row) => (
          <div>
            <div>{formatLiveLectureDate(row.startAt)}</div>
            <div className="text-muted small">
              {formatLiveLectureTimeSlot(row.startAt, row.endAt)}
            </div>
          </div>
        ),
        sortable: true,
      },
      {
        name: <ColumnHeader title="duration" />,
        cell: (row) => (
          <TextColumn
            text={`${row.durationMinutes} ${t("minutes")}`}
            isNeedToTranslate={false}
          />
        ),
      },
      {
        name: <ColumnHeader title="status" />,
        cell: (row) => (
          <TextColumn text={t(`liveLectureStatus_${row.status}`)} />
        ),
      },
      {
        name: <ColumnHeader title="action" />,
        cell: (row) => (
          <div className="d-flex align-items-center gap-1">
            {canManageLiveLecture(row, profile) &&
              joinableStatuses.includes(row.status) && (
                <Button
                  color="soft-primary"
                  size="sm"
                  onClick={() => onJoin(row)}
                  disabled={joiningId === row.id}
                  title={t("joinLiveLecture")}
                >
                  {joiningId === row.id ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="ri-live-line"></i>
                  )}
                </Button>
              )}
            {canManageLiveLecture(row, profile) &&
              canEditLiveLecture(row) && (
                <ActionButton
                  onAction={() => onEdit(row.id)}
                  actionType={ActionTypeEnum.EDIT}
                />
              )}
            {canManageLiveLecture(row, profile) && (
              <ActionButton
                onAction={() => onDelete(row.id)}
                actionType={ActionTypeEnum.DELETE}
              />
            )}
          </div>
        ),
      },
    ],
    [lang, t, profile, joiningId],
  );

  const onDeleteConfirm = async () => {
    if (!deletingItemId) return;
    await deleteLiveLecture(deletingItemId).unwrap();
    setDeletingItemId(null);
    setShowDeleteModal(false);
  };

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText, page: 1 });
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number,
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (
    column: TableColumn<LiveLectureDto>,
    sortDirection: string,
  ) => {
    const sortBy =
      column.id === 1
        ? `title.${i18n.language}:${sortDirection.toUpperCase()}`
        : `start_at:${sortDirection.toUpperCase()}`;
    setPaginateQuery({ ...paginateQuery, sortBy });
  };

  if (!isSuperAdmin) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center text-muted py-5">{t("unauthorized")}</div>
        </Container>
      </div>
    );
  }

  document.title = `${t("documentTitle", { pageName: "meetings" })}`;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("meetings")} pageTitle="dashboard" />
          <Row className="mt-2 mb-4 align-items-center">
            <Col className="col-sm-auto">
              <h4 className="mb-0">{t("meetings")}</h4>
              <p className="text-muted small mb-0 mt-1">
                {t("privateLiveLectureHint")}
              </p>
            </Col>
            <Col className="col-sm">
              <div className="d-flex justify-content-sm-end gap-2">
                <Button
                  color="success"
                  onClick={() => {
                    setPaginateQuery({ ...paginateQuery });
                    refetch();
                  }}
                >
                  {t("refresh")}
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    <Col md={6}>
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => onSearch(event.target.value)}
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col md={6} className="text-end">
                      <Button color="success" onClick={onAdd}>
                        <i className="ri-add-line align-bottom me-1"></i>
                        {t("addPrivateLiveLecture")}
                      </Button>
                    </Col>
                  </Row>
                  <AppTable
                    columns={columns}
                    data={tableData}
                    progressPending={isLoading}
                    progressComponent={<TableSpinner />}
                    pagination
                    paginationServer
                    sortServer
                    onSort={onSort}
                    onChangeRowsPerPage={onChangeRowsPerPage}
                    onChangePage={onChangePage}
                    paginationTotalRows={response?.meta?.totalItems}
                    paginationComponentOptions={{
                      rowsPerPageText: t("rowsPerPage"),
                      rangeSeparatorText: t("of"),
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <AddEditPrivateLiveLectureModal
        key={editingItemId || "create-private"}
        id={editingItemId}
        show={showAddEditModal}
        onCloseClick={() => {
          setShowAddEditModal(false);
          setEditingItemId(null);
          refetch();
        }}
      />

      <DeleteModal
        loading={isLoadingDelete}
        show={showDeleteModal}
        onDeleteClick={onDeleteConfirm}
        onCloseClick={() => setShowDeleteModal(false)}
      />
    </React.Fragment>
  );
};
